import { useEffect, useRef, useState } from "react";
import JSZip from "jszip";
import { api, API_BASE } from "@/lib/api";
import { Loader2, ImageDown, UploadCloud, HardDrive, Database, Trash2 } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { name: "address_line1", label: "Address line 1" },
  { name: "address_line2", label: "Address line 2 (city/state/zip)" },
  { name: "phone", label: "Phone" },
  { name: "email", label: "Email" },
  { name: "facebook", label: "Facebook URL" },
  { name: "instagram", label: "Instagram URL" },
  { name: "youtube", label: "YouTube URL" },
  { name: "donate_paypal_url", label: "PayPal fundraiser URL" },
  { name: "masjidal_monthly_url", label: "Masjidal monthly widget URL" },
  { name: "legal_name", label: "Legal name (501(c)(3)) — shown on donation receipts" },
  { name: "ein", label: "EIN / Federal Tax ID — shown on donation receipts" },
];

const TEXTAREAS = [
  { name: "about_mission", label: "Mission statement" },
  { name: "about_vision", label: "Vision statement" },
];

export default function SettingsAdmin() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/settings").then((r) => setData(r.data)); }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/settings", data); toast.success("Saved"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="text-ink/40">Loading…</div>;

  return (
    <div data-testid="admin-settings-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Manage</div>
        <h1 className="font-serif text-4xl mt-2 text-ink">Site Settings</h1>
      </div>
      <div className="rounded-3xl bg-white border border-sand p-8 md:p-10 max-w-3xl space-y-5">
        <div className="grid md:grid-cols-2 gap-5">
          {FIELDS.map((f) => (
            <div key={f.name} className={f.name.includes("address") || f.name === "phone" || f.name === "email" ? "" : "md:col-span-2"}>
              <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">{f.label}</label>
              <input
                type="text"
                value={data[f.name] ?? ""}
                onChange={(e) => setData({ ...data, [f.name]: e.target.value })}
                data-testid={`setting-${f.name}`}
                className="mt-2 w-full px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-emerald_deep"
              />
            </div>
          ))}
        </div>
        {TEXTAREAS.map((f) => (
          <div key={f.name}>
            <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">{f.label}</label>
            <textarea
              rows={4}
              value={data[f.name] ?? ""}
              onChange={(e) => setData({ ...data, [f.name]: e.target.value })}
              data-testid={`setting-${f.name}`}
              className="mt-2 w-full px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-emerald_deep resize-none"
            />
          </div>
        ))}
        <button onClick={save} disabled={saving} data-testid="settings-save"
                className="inline-flex items-center gap-2 rounded-full bg-emerald_deep text-cream px-8 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-emerald_deep-900 disabled:opacity-60">
          {saving && <Loader2 size={14} className="animate-spin" />} Save
        </button>

        <StorageStatsPanel />
        <ImageMigrationPanel />
      </div>
    </div>
  );
}


function StorageStatsPanel() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purging, setPurging] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/maintenance/storage-stats");
      setStats(r.data);
    } catch {/* ignore */} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const runEventPurge = async () => {
    if (!window.confirm(`Delete event flyer images for events older than ${stats?.retention_days || 365} days?`)) return;
    setPurging(true);
    try {
      const r = await api.post("/admin/maintenance/purge-old-event-images");
      toast.success(`Purged ${r.data.deleted} old event image(s)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge failed");
    } finally { setPurging(false); }
  };

  if (loading) return <div className="mt-6 text-sm text-ink/55">Loading storage stats…</div>;
  if (!stats) return null;

  const u = stats.uploads || {};
  const totalMb = (u.active_mb || 0) + (u.deleted_mb || 0);
  // M0 free tier is 512 MB across the cluster — show as guideline, not hard limit
  const M0_MB = 512;
  const pct = Math.min(100, Math.round(((u.active_mb || 0) / M0_MB) * 100));
  const bar_color = pct < 50 ? "bg-emerald-500" : pct < 80 ? "bg-amber-500" : "bg-rose-600";

  return (
    <div className="mt-8 pt-8 border-t border-sand">
      <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold mb-1 flex items-center gap-2">
        <HardDrive size={12} /> Storage
      </div>
      <h3 className="font-serif text-2xl text-ink">MongoDB usage</h3>

      {/* Upload bytes overview */}
      <div className="mt-5 rounded-2xl border border-sand bg-cream/40 p-5" data-testid="storage-uploads">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <div className="font-serif text-3xl text-navy tabular-nums">{u.active_mb} MB</div>
            <div className="text-[11px] uppercase tracking-wider text-ink/55 mt-0.5">{u.active_count} active uploads</div>
          </div>
          <div className="text-right text-xs text-ink/60">
            <div>{u.deleted_mb} MB in {u.deleted_count} soft-deleted file{u.deleted_count === 1 ? "" : "s"}</div>
            <div className="text-[11px] text-ink/40">(soft-deleted = removed but storage not yet reclaimed)</div>
          </div>
        </div>
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-wider text-ink/55 mb-1">vs. Atlas M0 free tier (512 MB cluster cap)</div>
          <div className="w-full h-2 rounded-full bg-sand overflow-hidden">
            <div className={`h-full transition-all ${bar_color}`} style={{ width: `${pct}%` }} />
          </div>
          <div className="text-[11px] text-ink/55 mt-1.5 tabular-nums">{pct}% of 512 MB used by uploads in this DB</div>
        </div>

        {Object.keys(u.by_source || {}).length > 0 && (
          <div className="mt-5 pt-4 border-t border-sand grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
            {Object.entries(u.by_source).map(([k, v]) => (
              <div key={k} className="rounded-xl bg-white border border-sand p-3">
                <div className="text-[10px] uppercase tracking-wider text-ink/50">{k}</div>
                <div className="font-serif text-lg text-navy mt-0.5 tabular-nums">{(v.bytes / 1024 / 1024).toFixed(1)} MB</div>
                <div className="text-[11px] text-ink/55">{v.count} file{v.count === 1 ? "" : "s"}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pending purge */}
      <div className="mt-4 rounded-2xl border border-sand bg-white p-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gold font-semibold">Auto-purge pending</div>
          <div className="font-serif text-xl text-ink mt-0.5">{stats.event_images_pending_purge} event image{stats.event_images_pending_purge === 1 ? "" : "s"} eligible for cleanup</div>
          <div className="text-[11px] text-ink/55 mt-1">Events older than {stats.retention_days} days. Auto-runs daily at 03:30 ET — or run now.</div>
        </div>
        <button
          onClick={runEventPurge}
          disabled={purging || stats.event_images_pending_purge === 0}
          data-testid="storage-run-purge"
          className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-5 py-2 text-xs font-semibold uppercase tracking-[0.18em] hover:bg-navy-900 disabled:opacity-40"
        >
          {purging ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
          Run purge now
        </button>
      </div>

      {/* Collection counts */}
      <details className="mt-4 rounded-2xl border border-sand bg-white p-5">
        <summary className="cursor-pointer text-sm font-medium text-ink flex items-center gap-2">
          <Database size={14} className="text-gold" /> Document counts by collection
        </summary>
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-2 text-xs">
          {Object.entries(stats.collections || {}).map(([k, v]) => (
            <div key={k} className="flex justify-between bg-cream/60 rounded-lg px-3 py-2 font-mono">
              <span className="text-ink/65">{k}</span>
              <span className="text-navy font-semibold tabular-nums">{v}</span>
            </div>
          ))}
        </div>
      </details>

      <div className="mt-3 text-right">
        <button onClick={load} className="text-xs text-ink/50 hover:text-navy underline-offset-4 hover:underline">
          Refresh stats
        </button>
        <span className="text-ink/30 mx-2">·</span>
        <span className="text-xs text-ink/50">Total occupied: <span className="text-navy font-medium">{totalMb.toFixed(1)} MB</span></span>
      </div>
    </div>
  );
}


function ImageMigrationPanel() {
  const [running, setRunning] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [purging, setPurging] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, current: "" });
  const [report, setReport] = useState(null);
  const fileRef = useRef(null);

  const runPurge = async () => {
    if (!window.confirm("Delete any mirrored images that are NOT actually referenced on the site (e.g. WordPress thumbnail variants from a previous aborted upload)? This frees DB storage. Safe to run.")) return;
    setPurging(true);
    setReport(null);
    try {
      const r = await api.post("/admin/maintenance/purge-unreferenced-mirrored", {}, { timeout: 600000 });
      setReport(r.data);
      const mb = ((r.data.bytes_freed || 0) / 1024 / 1024).toFixed(1);
      toast.success(`Cleaned up — deleted ${r.data.deleted || 0} unused images (${mb} MB freed)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cleanup failed");
    } finally { setPurging(false); }
  };

  const runWayback = async () => {
    if (!window.confirm("Mirror every flintislamiccenter.com image into your MongoDB via Wayback Machine? Safe to re-run.")) return;
    setRunning(true);
    setReport(null);
    try {
      const r = await api.post("/admin/maintenance/mirror-wp-images", {}, { timeout: 600000 });
      setReport(r.data);
      toast.success(`Done — ${r.data.urls_mirrored || 0} images mirrored`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Migration failed");
    } finally { setRunning(false); }
  };

  const onZipPicked = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".zip")) {
      toast.error("Please upload a .zip file");
      return;
    }
    if (!window.confirm(`Smart-import from ${f.name} (${(f.size / 1024 / 1024).toFixed(1)} MB)? Only images actually referenced on your site will be uploaded — WordPress thumbnail variants will be skipped automatically.`)) {
      e.target.value = "";
      return;
    }
    setUploading(true);
    setReport(null);
    setProgress({ done: 0, total: 0, current: "Scanning database for required images…" });
    try {
      // 1. Ask backend which URLs are actually referenced in the site
      const neededRes = await api.get("/admin/maintenance/wp-urls-needed");
      const pending = new Set(neededRes.data.pending || []);
      const totalNeeded = neededRes.data.total_needed || 0;
      const alreadyMirrored = neededRes.data.already_mirrored || 0;
      if (pending.size === 0) {
        toast.success(`All ${totalNeeded} required images are already mirrored — nothing to do!`);
        setReport({ ok: true, total_needed: totalNeeded, already_mirrored: alreadyMirrored, pending: 0 });
        setUploading(false);
        e.target.value = "";
        return;
      }

      // 2. Unzip in browser & match only what's needed
      setProgress({ done: 0, total: 0, current: `Unzipping & matching ${pending.size} required images…` });
      const zip = await JSZip.loadAsync(f);
      const imageExt = /\.(jpe?g|png|webp|gif|svg)$/i;
      const matched = [];   // { entry, rel, canonical }
      const missing = [];   // canonical URLs we couldn't find in the ZIP
      const matchedSet = new Set();
      zip.forEach((relPath, entry) => {
        if (entry.dir) return;
        if (!imageExt.test(relPath)) return;
        let rel = relPath.replace(/\\/g, "/");
        rel = rel.replace(/^wp-content\//i, "");
        rel = rel.replace(/^uploads\//i, "");
        if (rel.startsWith("__MACOSX/") || rel.includes("/.DS_Store")) return;
        const canonical = `https://flintislamiccenter.com/wp-content/uploads/${rel}`;
        if (pending.has(canonical) && !matchedSet.has(canonical)) {
          matched.push({ entry, rel, canonical });
          matchedSet.add(canonical);
        }
      });
      // Identify still-missing
      pending.forEach((u) => { if (!matchedSet.has(u)) missing.push(u); });
      const total = matched.length;
      if (total === 0) {
        throw new Error(`Found ${pending.size} pending images in DB but none matched any file in your ZIP. Path mismatch?`);
      }
      setProgress({ done: 0, total, current: "" });
      const token = localStorage.getItem("fic_token");

      let imported = 0;
      let skipped = 0;
      let failed = 0;
      const failures = [];

      // 3. Sequential upload — only what we actually need
      for (let i = 0; i < matched.length; i++) {
        const { entry, rel, canonical } = matched[i];
        setProgress({ done: i, total, current: rel });
        try {
          const blob = await entry.async("blob");
          const fd = new FormData();
          fd.append("canonical_url", canonical);
          fd.append("file", blob, rel.split("/").pop());
          const r = await fetch(`${API_BASE}/admin/maintenance/import-wp-image`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: fd,
          });
          if (!r.ok) {
            failed++;
            failures.push(`${rel} (HTTP ${r.status})`);
            continue;
          }
          const data = await r.json();
          if (data.skipped) skipped++; else imported++;
        } catch (err) {
          failed++;
          failures.push(`${rel}: ${err.message}`);
        }
      }
      setProgress({ done: total, total, current: "Rewriting URLs in database…" });
      // 4. Run the DB rewrite — instant since url_mirrors is now populated
      let dbStats = {};
      try {
        const r2 = await api.post("/admin/maintenance/mirror-wp-images", {}, { timeout: 600000 });
        dbStats = r2.data || {};
      } catch (err) {
        toast.error(`DB rewrite failed: ${err.message}`);
      }
      setReport({
        ok: true,
        total_needed_in_db: totalNeeded,
        already_mirrored_before: alreadyMirrored,
        matched_in_zip: matched.length,
        imported,
        skipped_already_mirrored: skipped,
        failed,
        zip_failures: failures.slice(0, 20),
        missing_from_zip: missing.slice(0, 20),
        ...dbStats,
      });
      toast.success(`Done — imported ${imported} required images${skipped ? ` (${skipped} already mirrored)` : ""}${failed ? `, ${failed} failed` : ""}`);
    } catch (err) {
      toast.error(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="mt-8 pt-8 border-t border-sand">
      <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold mb-1">⚠ Image Migration</div>
      <h3 className="font-serif text-2xl text-ink">Rescue old WP images → host on MongoDB</h3>
      <p className="text-sm text-ink/65 mt-2 leading-relaxed max-w-2xl">
        After your DNS cutover, images served from <code className="bg-sand/50 px-1.5 py-0.5 rounded text-[12px]">flintislamiccenter.com/wp-content/uploads/...</code> stopped loading because that hostname now points at this new site. Run <strong>one</strong> of the options below to copy them all into your own MongoDB. Then you can cancel Bluehost without losing a single image.
      </p>

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        {/* Path 1: Wayback */}
        <div className="rounded-2xl border border-sand bg-cream/40 p-5">
          <div className="flex items-center gap-2 text-navy">
            <ImageDown size={18} className="text-gold" />
            <h4 className="font-serif text-lg">Option 1 — Try Wayback Machine</h4>
          </div>
          <p className="text-xs text-ink/60 mt-2 leading-relaxed">
            Automatic. Fetches each broken image from web.archive.org. Best-effort — may miss images Wayback never indexed.
          </p>
          <button
            onClick={runWayback}
            disabled={running}
            data-testid="migrate-wayback-btn"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy text-cream px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] hover:bg-navy-900 disabled:opacity-60"
          >
            {running ? <Loader2 size={14} className="animate-spin" /> : <ImageDown size={14} />}
            {running ? "Running…" : "Mirror via Wayback"}
          </button>
        </div>

        {/* Path 2: ZIP */}
        <div className="rounded-2xl border-2 border-gold bg-gold/5 p-5">
          <div className="flex items-center gap-2 text-navy">
            <UploadCloud size={18} className="text-gold" />
            <h4 className="font-serif text-lg">Option 2 — Upload uploads.zip <span className="text-[10px] uppercase tracking-wider bg-gold text-navy px-2 py-0.5 rounded-full ml-1 align-middle">recommended</span></h4>
          </div>
          <p className="text-xs text-ink/60 mt-2 leading-relaxed">
            <strong>Smart import.</strong> The browser scans your DB first, then uploads ONLY images actually used on the site (skips WordPress thumbnail variants like <code className="text-[10px]">-150x150</code>, <code className="text-[10px]">-370x450</code>, etc.). 100% reliable, no proxy limits, no DB bloat.
          </p>
          <input
            ref={fileRef}
            type="file"
            accept=".zip"
            onChange={onZipPicked}
            data-testid="migrate-zip-input"
            disabled={uploading}
            className="hidden"
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="migrate-zip-btn"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold text-navy px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] hover:bg-gold-600 disabled:opacity-60"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}
            {uploading ? "Uploading…" : "Choose uploads.zip"}
          </button>
        </div>
      </div>

      {uploading && progress.total > 0 && (
        <div className="mt-6 rounded-2xl bg-cream/40 border border-sand p-5" data-testid="migrate-progress">
          <div className="flex items-center justify-between text-xs mb-2">
            <span className="text-ink/70 font-medium">{progress.done} / {progress.total}</span>
            <span className="text-ink/50 font-mono truncate ml-3">{progress.current}</span>
          </div>
          <div className="w-full h-2 rounded-full bg-sand overflow-hidden">
            <div
              className="h-full bg-gold transition-all duration-200"
              style={{ width: `${Math.round((progress.done / Math.max(1, progress.total)) * 100)}%` }}
            />
          </div>
          <p className="mt-3 text-[11px] text-ink/55 leading-relaxed">
            Browser is unzipping locally and uploading each image one-by-one — safe for any ZIP size, no proxy limits.
            You can keep this tab open in the background.
          </p>
        </div>
      )}

      {/* Cleanup button — for after an aborted run that imported unwanted thumbnails */}
      <div className="mt-4 text-right">
        <button
          onClick={runPurge}
          disabled={purging || uploading || running}
          data-testid="migrate-cleanup-btn"
          className="text-xs text-ink/50 hover:text-rose-600 underline-offset-4 hover:underline disabled:opacity-40"
        >
          {purging ? "Cleaning up…" : "Clean up unused thumbnails (frees DB space)"}
        </button>
      </div>

      {report && (
        <div className="mt-6 rounded-xl bg-navy text-cream p-5 text-xs space-y-1.5 font-mono" data-testid="migrate-report">
          <div className="text-gold uppercase tracking-wider mb-2">Result</div>
          {Object.entries(report).map(([k, v]) => (
            <div key={k}>
              <span className="text-cream/60">{k}:</span> <span className="text-cream">
                {Array.isArray(v) ? `[${v.length}]` : typeof v === "object" ? JSON.stringify(v) : String(v)}
              </span>
            </div>
          ))}
        </div>
      )}

      <GalleryBulkImporter />
    </div>
  );
}

function GalleryBulkImporter() {
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, current: "" });
  const [report, setReport] = useState(null);
  const [folderFilter, setFolderFilter] = useState("");
  const [excludeThumbnails, setExcludeThumbnails] = useState(true);
  const [clearFirst, setClearFirst] = useState(false);
  const fileRef = useRef(null);

  const onZipPicked = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".zip")) {
      toast.error("Please upload a .zip file");
      return;
    }
    const filterDesc = folderFilter ? `containing "${folderFilter}"` : "(all folders)";
    if (!window.confirm(
      `Bulk-import every photo from ${f.name} ${filterDesc} into the public Gallery?\n\n` +
      `${excludeThumbnails ? "Thumbnails (-NNNxNNN suffixes) will be skipped.\n" : ""}` +
      `${clearFirst ? "⚠ Existing gallery will be DELETED first.\n" : ""}` +
      `\nThis is one-way — make sure you're ready.`
    )) {
      e.target.value = "";
      return;
    }
    setImporting(true);
    setReport(null);
    try {
      // Optional clear
      if (clearFirst) {
        await api.post("/admin/maintenance/clear-gallery");
      }
      const zip = await JSZip.loadAsync(f);
      const imageExt = /\.(jpe?g|png|webp|gif)$/i;       // skip .svg for gallery
      const thumbPattern = /-\d{2,4}x\d{2,4}\.(jpe?g|png|webp)$/i;
      const matched = [];
      zip.forEach((relPath, entry) => {
        if (entry.dir) return;
        if (!imageExt.test(relPath)) return;
        let rel = relPath.replace(/\\/g, "/");
        rel = rel.replace(/^wp-content\//i, "").replace(/^uploads\//i, "");
        if (rel.startsWith("__MACOSX/") || rel.includes("/.DS_Store")) return;
        if (excludeThumbnails && thumbPattern.test(rel)) return;
        if (folderFilter && !rel.toLowerCase().includes(folderFilter.toLowerCase())) return;
        matched.push({ entry, rel });
      });
      const total = matched.length;
      if (total === 0) {
        throw new Error(`No photos matched. Try removing the folder filter or unchecking "exclude thumbnails".`);
      }
      // Hard cap to prevent another 15K incident
      if (total > 800) {
        if (!window.confirm(`That will import ${total} photos to your Gallery. Continue?`)) {
          setImporting(false);
          e.target.value = "";
          return;
        }
      }
      setProgress({ done: 0, total, current: "" });
      const token = localStorage.getItem("fic_token");

      let imported = 0;
      let failed = 0;
      const failures = [];

      for (let i = 0; i < matched.length; i++) {
        const { entry, rel } = matched[i];
        setProgress({ done: i, total, current: rel });
        try {
          const blob = await entry.async("blob");
          const fd = new FormData();
          fd.append("file", blob, rel.split("/").pop());
          const r = await fetch(`${API_BASE}/admin/maintenance/import-gallery-image`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: fd,
          });
          if (!r.ok) {
            failed++;
            failures.push(`${rel} (HTTP ${r.status})`);
            continue;
          }
          imported++;
        } catch (err) {
          failed++;
          failures.push(`${rel}: ${err.message}`);
        }
      }
      setProgress({ done: total, total, current: "Done" });
      setReport({ ok: true, matched: total, imported, failed, failures: failures.slice(0, 10) });
      toast.success(`Gallery imported — ${imported} photos${failed ? `, ${failed} failed` : ""}`);
    } catch (err) {
      toast.error(`Bulk import failed: ${err.message}`);
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  return (
    <div className="mt-10 pt-8 border-t border-sand">
      <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold mb-1">📸 Bulk Gallery Importer</div>
      <h3 className="font-serif text-2xl text-ink">Populate the public Photo Gallery from a ZIP</h3>
      <p className="text-sm text-ink/65 mt-2 leading-relaxed max-w-2xl">
        The public gallery (<code className="bg-sand/50 px-1.5 py-0.5 rounded text-[12px]">/gallery</code>) is currently empty. Use this to upload a batch of community photos directly from the same ZIP — they'll appear instantly on the public site. <strong>Different from the migration above:</strong> this creates new gallery entries instead of patching old WP URLs.
      </p>

      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.2em] text-ink/60 font-semibold">Folder filter (optional)</span>
          <input
            value={folderFilter}
            onChange={(ev) => setFolderFilter(ev.target.value)}
            data-testid="gallery-folder-filter"
            placeholder='e.g. "2024/06" or "events"'
            className="mt-1 w-full px-3 py-2 rounded-lg border border-sand text-sm focus:outline-none focus:border-navy"
          />
          <span className="text-[11px] text-ink/45 mt-1 block">Only files whose path contains this string will be imported.</span>
        </label>
        <div className="space-y-2 self-end">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={excludeThumbnails} onChange={(ev) => setExcludeThumbnails(ev.target.checked)} className="w-4 h-4 accent-navy" />
            <span>Skip WP thumbnail variants (recommended)</span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={clearFirst} onChange={(ev) => setClearFirst(ev.target.checked)} className="w-4 h-4 accent-rose-600" />
            <span className="text-rose-700">Clear existing gallery first ⚠</span>
          </label>
        </div>
      </div>

      <input ref={fileRef} type="file" accept=".zip" onChange={onZipPicked} disabled={importing} className="hidden" />
      <button
        onClick={() => fileRef.current?.click()}
        disabled={importing}
        data-testid="gallery-import-btn"
        className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-navy text-cream px-6 py-3 text-xs font-semibold uppercase tracking-[0.2em] hover:bg-navy-900 disabled:opacity-60"
      >
        {importing ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}
        {importing ? "Importing…" : "Choose ZIP & import to Gallery"}
      </button>

      {importing && progress.total > 0 && (
        <div className="mt-5 rounded-xl bg-cream/40 border border-sand p-4">
          <div className="flex items-center justify-between text-xs mb-2">
            <span className="text-ink/70 font-medium">{progress.done} / {progress.total}</span>
            <span className="text-ink/50 font-mono truncate ml-3">{progress.current}</span>
          </div>
          <div className="w-full h-2 rounded-full bg-sand overflow-hidden">
            <div className="h-full bg-navy" style={{ width: `${Math.round((progress.done / Math.max(1, progress.total)) * 100)}%` }} />
          </div>
        </div>
      )}

      {report && (
        <div className="mt-5 rounded-xl bg-navy text-cream p-4 text-xs font-mono space-y-1">
          <div className="text-gold uppercase tracking-wider mb-1">Gallery Import Result</div>
          {Object.entries(report).map(([k, v]) => (
            <div key={k}>
              <span className="text-cream/60">{k}:</span> <span>{Array.isArray(v) ? `[${v.length}]` : String(v)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
